import { useCallback, useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { updateWidgetData as updateWidgetDataAction, addWidgetSection, updateSiteContentRequest, selectPlainSiteContent } from '../redux/slices/siteContentSlice';
import { toggleEditMode } from '../redux/slices/editModeSlice';
import { Widget } from '../utils/widgetUtils';
import { useSelector } from 'react-redux';
import { WidgetType } from '../widgets/widgetRegistry';

export interface PageWidget {
  type: WidgetType;
  data: any;
  index: number;
}

export const useDynamicPageWidgets = (pageType: string) => {
  const siteContent = useSelector(selectPlainSiteContent);
  const { isEditMode } = useAppSelector((state) => state.editMode);
  const { updateLoading, error } = useAppSelector((state) => state.siteContent);
  const dispatch = useAppDispatch();
  
  // Find all widgets for this page type
  const pageWidgets = siteContent?.widgets?.filter((widget: Widget) => {
    // Match widgets by page type - could be exact match or contains logic
    return widget.type.toLowerCase().includes(pageType.toLowerCase()) ||
           widget.pageType === pageType ||
           (pageType === 'story' && widget.type.toLowerCase().includes('story')) ||
           (pageType === 'blog' && widget.type.toLowerCase().includes('blog')) ||
           (pageType === 'events' && widget.type.toLowerCase().includes('event')) ||
           (pageType === 'gallery' && widget.type.toLowerCase().includes('gallery')) ||
           (pageType === 'contact' && widget.type.toLowerCase().includes('contact'));
  }) || [];

  // Map widgets with their indices for updates
  const mappedWidgets: PageWidget[] = pageWidgets.map((widget: Widget) => {
    const originalIndex = siteContent?.widgets?.findIndex((w: Widget) => w === widget) || 0;
    return {
      type: widget.type as WidgetType,
      data: widget.data,
      index: originalIndex
    };
  });

  // Local draft state for all widgets
  const [localWidgets, setLocalWidgets] = useState<PageWidget[]>(mappedWidgets);
  const [isSaving, setIsSaving] = useState(false);

  // Sync local when Redux data changes
  useEffect(() => {
    if (mappedWidgets.length > 0) {
      setLocalWidgets(mappedWidgets);
    }
  }, [siteContent]);

  // Reset saving state when loading completes
  useEffect(() => {
    if (!updateLoading && isSaving) {
      setIsSaving(false);
      // Force sync local data with Redux after save completes
      if (mappedWidgets.length > 0) {
        setLocalWidgets(mappedWidgets);
      }
    }
  }, [updateLoading, isSaving, mappedWidgets]);

  // Update specific widget data locally
  const updateWidgetData = useCallback((widgetIndex: number, newData: any) => {
    setLocalWidgets(prev => 
      prev.map((widget, idx) => 
        idx === widgetIndex 
          ? { ...widget, data: newData }
          : widget
      )
    );
  }, []);

  // Save all widgets to Redux + backend
  const saveWidgets = useCallback(async () => {
    if (localWidgets.length === 0) return;

    setIsSaving(true);
    
    // Update each widget in Redux
    localWidgets.forEach((widget) => {
      dispatch(updateWidgetDataAction({ index: widget.index, data: widget.data }));
    });

    // Then trigger backend update
    dispatch(updateSiteContentRequest());
    
    console.log(`[useDynamicPageWidgets] Saved ${pageType} page widgets:`, localWidgets);
  }, [dispatch, localWidgets, pageType]);

  // Toggle edit mode (save when closing edit mode)
  const toggleEditModeWithSave = useCallback(async () => {
    if (isEditMode) {
      await saveWidgets();
    }
    dispatch(toggleEditMode());
  }, [dispatch, isEditMode, saveWidgets]);

  // Add new widget to page
  const addWidget = useCallback((widgetType: WidgetType, widgetData: any) => {
    const newWidget = { type: widgetType, data: widgetData };
    dispatch(addWidgetSection({ widget: newWidget }));
  }, [dispatch]);

  // Force refresh local data from Redux
  const refreshLocalData = useCallback(() => {
    if (mappedWidgets.length > 0) {
      setLocalWidgets(mappedWidgets);
      console.log(`[useDynamicPageWidgets] Refreshed local data for ${pageType}:`, mappedWidgets);
    }
  }, [mappedWidgets, pageType]);

  return {
    widgets: localWidgets,
    updateWidgetData,
    saveWidgets,
    toggleEditModeWithSave,
    addWidget,
    refreshLocalData,
    isEditMode,
    isSaving,
    hasError: !!error,
    hasWidgets: localWidgets.length > 0
  };
};
